import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Send, Loader, MessageSquare, RefreshCw } from 'react-feather';

const api = process.env.REACT_APP_API_URL;

/**
 * Claude-powered chat panel for live resume editing.
 *
 * Props:
 *   onResumeUpdate(updatedResume), called whenever Claude returns an updated resume.
 */
// Mirrors the backend CHAT_MAX_MESSAGES setting (per resume session).
const MAX_MESSAGES = 2;

export default function ResumeChat({ onResumeUpdate }) {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: `Hi! I'm Claude. You can send me up to ${MAX_MESSAGES} messages to refine this resume, so make them count. Try asking me to:\n• Strengthen the summary\n• Add more impact to a bullet point\n• Make the skills section more concise\n• Tailor the resume for a specific role`,
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [remaining, setRemaining] = useState(MAX_MESSAGES); // shown from the start
  const [limitReached, setLimitReached] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading || limitReached) return;

    const userMsg = { role: 'user', content: text };
    const history = messages.filter(m => m.role !== 'system');

    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const { data } = await axios.post(
        `${api}chat-resume/`,
        { message: text, history },
        { withCredentials: true }
      );

      const assistantMsg = { role: 'assistant', content: data.reply };
      setMessages(prev => [...prev, assistantMsg]);

      if (data.updated_resume) {
        onResumeUpdate(data.updated_resume);
      }
      if (typeof data.remaining === 'number') {
        setRemaining(data.remaining);
        if (data.remaining <= 0) setLimitReached(true);
      }
    } catch (err) {
      const backendMsg = err?.response?.data?.error;
      if (err?.response?.data?.limit_reached) setLimitReached(true);
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: backendMsg ? `⚠️ ${backendMsg}` : '⚠️ Something went wrong. Please try again.' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const reset = () => {
    setMessages([{
      role: 'assistant',
      content: "Conversation cleared. How can I help with your resume?",
    }]);
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-violet-50 to-white">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-violet-600 flex items-center justify-center">
            <MessageSquare className="w-4 h-4 text-white" />
          </div>
          <div>
            <div className="text-sm font-semibold text-gray-800">Claude Resume Assistant</div>
            <div className="text-xs text-violet-500">Ask me to edit anything</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            title="Messages remaining for this resume"
            className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
              limitReached
                ? 'bg-gray-100 text-gray-400'
                : remaining === 1
                ? 'bg-amber-100 text-amber-700'
                : 'bg-violet-100 text-violet-700'
            }`}
          >
            {limitReached ? '0 left' : `${remaining}/${MAX_MESSAGES} left`}
          </span>
          <button
            onClick={reset}
            title="Clear conversation"
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-0">
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap leading-relaxed ${
                msg.role === 'user'
                  ? 'bg-blue-600 text-white rounded-br-sm'
                  : 'bg-gray-100 text-gray-800 rounded-bl-sm'
              }`}
            >
              {msg.content}
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 rounded-2xl rounded-bl-sm px-3 py-2 flex items-center gap-2">
              <Loader className="w-3.5 h-3.5 text-violet-500 animate-spin" />
              <span className="text-xs text-gray-500">Claude is thinking…</span>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="border-t border-gray-100 px-3 py-2.5 flex items-end gap-2">
        <textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKey}
          disabled={limitReached}
          placeholder={limitReached ? 'Chat limit reached for this resume' : 'Ask Claude to improve your resume…'}
          rows={2}
          className="flex-1 resize-none rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 focus:bg-white transition disabled:opacity-50 disabled:cursor-not-allowed"
        />
        <button
          onClick={send}
          disabled={!input.trim() || loading || limitReached}
          className="flex-shrink-0 w-9 h-9 rounded-xl bg-violet-600 text-white flex items-center justify-center hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
        >
          <Send className="w-4 h-4" />
        </button>
      </div>

      <div className="px-3 pb-2 text-center text-[10px] text-gray-400">
        {limitReached
          ? 'You’ve used all your AI chat messages for this resume'
          : remaining !== null
          ? `Changes appear live in the preview · ${remaining} message${remaining === 1 ? '' : 's'} left`
          : 'Changes appear live in the preview · Press Enter to send'}
      </div>
    </div>
  );
}
